import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { generateQuotePdf } from "@/lib/pdfLazy";
import { SignView } from "@/components/SignView";
import { PosterPreview } from "@/components/PosterPreview";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

// Page publique : le client consulte et signe son devis à distance via un lien personnel.
export default function RemoteSign() {
  const { t } = useI18n();
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdf, setPdf] = useState(null);

  useEffect(() => {
    api.get(`/public/sign/${token}`).then(({ data }) => setQuote(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || "Lien invalide."));
  }, [token]);

  const toPdf = async (q) => {
    const blob = await generateQuotePdf(q, { blob: true });
    const name = `Commande_${q.quote_number}.pdf`;
    return { blob, name, save: () => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); } };
  };

  const sign = async (sig) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/sign/${token}`, { signature: sig, cgv_accepted: true });
      setQuote(data);
      const p = await toPdf(data);
      const fd = new FormData();
      fd.append("file", new File([p.blob], p.name, { type: "application/pdf" }));
      await api.post(`/public/sign/${token}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } }).catch(() => {});
      setPdf({ data: await p.blob.arrayBuffer(), save: p.save });
      toast.success("Commande signée : une copie vous a été envoyée par e-mail.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const openSigned = async () => { const p = await toPdf(quote); setPdf({ data: await p.blob.arrayBuffer(), save: p.save }); };

  if (error) return <Message icon={<AlertTriangle className="w-10 h-10 text-destructive" />} text={error} testid="remote-sign-error" />;
  if (!quote) return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (quote.signature) {
    return (
      <Message icon={<CheckCircle2 className="w-10 h-10 text-primary" />} testid="remote-sign-done"
        text={`Commande ${quote.quote_number} signée. Merci ! Une copie vous a été envoyée par e-mail.`}>
        <button type="button" onClick={openSigned} className="mt-4 text-primary underline" data-testid="remote-sign-view-pdf">{t("Voir la commande signée")}</button>
        <PosterPreview pdf={pdf} onClose={() => setPdf(null)} printable testid="order-preview" title={t("Commande signée")} description={t("Vous pouvez télécharger ou imprimer votre commande.")} />
      </Message>
    );
  }
  return <SignView open remote onOpenChange={() => {}} quote={quote} confirmLabel="Signer et passer commande" onConfirm={sign} busy={busy} testid="remote-sign-confirm" />;
}

const Message = ({ icon, text, testid, children }) => (
  <div className="max-w-lg mx-auto text-center py-24 px-6" data-testid={testid}>
    <div className="flex justify-center mb-4">{icon}</div>
    <p className="text-lg">{text}</p>
    {children}
  </div>
);
